import { getAutoProps } from 'js/auto_props/client';

import { recordExtensionBucketing } from 'js/bulk_download/autotest/recordExtensionBucketing';
import type { BulkDownloadTrigger } from 'js/bulk_download/bulk_download_helper';

/**
 * The reader's arm of the post-pack fake door, or null for no funnel — meaning
 * the PDF Pack download shows the stock progress modal.
 *
 * The arm comes from the SERVER, in the `BulkDownloadFlags` auto-prop, because
 * the client-side `abTest()` helper has no rollout gate: it would bucket every
 * pack downloader the moment this ships, and this funnel replaces a screen
 * someone has just paid for. The server honours the staged rollout, so exposure
 * starts at nobody and is ramped deliberately.
 *
 * A PROP RATHER THAN AN ENDPOINT, which this used to be. `showBulkDownloadModal`
 * gates rendering the download modal on knowing the arm, so a fetch put a
 * network round trip in front of a paid-for download — bounded by a timeout
 * that still cost the reader up to a second of dead screen, and unbounded
 * before that. Reading a prop is synchronous, so that whole class of problem is
 * gone along with the endpoint, its controller and its route.
 *
 * The server resolves READ-ONLY on page render, so loading a page enrols
 * nobody, and this reader records nothing either: the bucketing is written when
 * the pack download actually STARTS (`PremiumWorkCardsBulkDownloadModal`, stage
 * `downloading`, through `recordFunnelExposure`), which is the moment the
 * funnel replaces. Opening the pack chooser and closing it enrols nobody.
 */
const TEST_KEY =
  'demo_day_2026_q3_reference_manager_and_chrome_extension_fake_door';

/**
 * The `?bulkDownload` return from checkout, which resumes the pack the reader
 * has just paid for (`useHeroDownloadState`, `WorkActions`).
 */
const CHECKOUT_RESUMPTION_TRIGGER = 'swp-auto-bulk-download';

/**
 * The controls that ask for a PDF PACK, and nothing else.
 *
 * THE SINGLE-PAPER DOWNLOAD IS NOT ON THIS LIST, DELIBERATELY. A premium
 * reader who clicks "Download PDF" is asking for the one paper in front of
 * them; `showBulkDownloadModal` routes them through the same premium modal, so
 * without this allowlist the funnel would replace the screen after a
 * single-paper download too and the wave would be measuring the wrong intent.
 * That excludes `swp-download-button` and every `*-work-card-download-button`
 * (including the heroes' own single-pdf trigger), plus the related-work rails
 * `swp-right-rail-related-work` and `swp-related-works-grid`, which download
 * the one related paper that was clicked.
 *
 * Nor is anything that opened BY ITSELF: `swp-auto-download` is the
 * interruption chooser and the email deep link, and
 * `swp-auto-bulk-download-books` is the BDB email redirect. A pack modal nobody
 * asked for is not a reader downloading a pack.
 *
 * The reg-path pack triggers (`sutd-bulk-download`, `sutd-post-download-pack`)
 * are also out: those pages never register `BulkDownloadFlags`, so there is no
 * arm to read there anyway, and their readers are not premium.
 */
const PACK_DOWNLOAD_TRIGGERS: Partial<Record<BulkDownloadTrigger, true>> = {
  'swp-bulk-download-button': true,
  'swp-bulk-download-preview-button': true,
  'swp-sticky-header-bulk-download-button': true,
  /*
   * The LISWP-blocks heroes (`hero_tabs`, `hero_itemized`) open the PACK modal
   * with their bare `heroSource` — `swp-tabs-hero` is the only live value —
   * while `useDownload` derives `swp-tabs-hero-work-card-download-button` for
   * the SINGLE pdf off the same hero. One belongs here and the other does not,
   * so they are listed apart rather than matched by prefix.
   */
  'swp-tabs-hero': true,
  'news-feed-bulk-download-button': true,
  'search-results-bulk-download-button': true,
  'library-bulk-download-button': true,
  'library-pdf-packs-card': true,
  'recommended-pdf-packs-card': true,
  'recommended-pdf-packs-deep-link': true,
};

export type PackDownloadMoment = {
  /** Which surface opened the modal; see `BULK_DOWNLOAD_IMPRESSION_TRIGGERS`. */
  trigger?: string;
  /** `Aedu.User.current.premium`, baked in when the page renders. */
  isPremium: boolean;
  /** Server's post-upgrade grace window; see `BulkDownload::PostUpgradeAccess`. */
  postUpgradeAccess: boolean;
};

/**
 * The arm to carry into this pack download, or null to leave the stock progress
 * modal alone. Pure — nothing is recorded here.
 *
 * TWO MOMENTS, AND NOTHING ELSE. The condition that routes a reader to the
 * premium pack modal at all (`isPremium || purchased || postUpgradeAccess`) is
 * far wider than either of them: it takes in a three-year subscriber
 * auto-interrupted mid-read who asked for nothing, and a free reader
 * re-downloading a pack they bought six months ago. Bucketing those readers
 * spends the sample on intent this pitch was never aimed at, and dilutes every
 * guardrail read against the bucketed side.
 *
 * 1. POST-CHECKOUT — the resumption trigger above, or the server's 10-minute
 *    `postUpgradeAccess` window on one of the pack controls below. The window
 *    is what identifies the reader who has just paid: `premium` is baked into
 *    the page at render, so it is still false for them and moment 2 cannot
 *    catch them.
 * 2. A PREMIUM READER DOWNLOADING A PACK — premium, plus one of the pack
 *    controls in `PACK_DOWNLOAD_TRIGGERS`: the LISWP PDF Pack buttons, the
 *    sticky header, the work-card pack buttons and the /pdf_packs cards and
 *    deep links. NOT the single-paper "Download PDF" button.
 *
 * THE ALLOWLIST BINDS BOTH MOMENTS, which is why `postUpgradeAccess` is not
 * sufficient on its own. Four call sites now forward that flag, two of them
 * manual CTAs, and the authorization mirror it belongs to (`useDownload`, the
 * single-paper gate) would be a reasonable fifth — at which point an
 * unqualified post-upgrade branch would silently re-admit the single-paper
 * download this allowlist exists to keep out. The resumption trigger is
 * exempt because it IS the post-checkout pack download, under its own name.
 */
export function referenceManagerFunnelArm({
  trigger,
  isPremium,
  postUpgradeAccess,
}: PackDownloadMoment): string | null {
  const packControl = Object.hasOwn(PACK_DOWNLOAD_TRIGGERS, trigger ?? '');
  const postCheckout =
    trigger === CHECKOUT_RESUMPTION_TRIGGER ||
    (postUpgradeAccess && packControl);
  const premiumPackDownload = isPremium && packControl;
  if (!postCheckout && !premiumPackDownload) return null;

  try {
    return getAutoProps('BulkDownloadFlags')?.postPackVariant ?? null;
  } catch {
    /*
     * Most pages never register these flags and `getAutoProps` throws rather
     * than returning undefined there. Not an error for this reader: it just
     * means no funnel. Mirrors `quietExtensionVariant`.
     */
    return null;
  }
}

/**
 * Record the exposure, at the start of the pack download the arm was resolved
 * for. `control` and `holdout` are recorded like any other arm — a denominator
 * needs them — and render the stock modal.
 *
 * A FORCED ARM IS SHOWN AND NEVER RECORDED. The `?ab_test[...]` override rides
 * on the page request, so this POST carries none of it: the server would find
 * no override, pass the rollout gate, disagree with the forced arm, raise a
 * Sentry variant mismatch, and write a user_tests row for traffic the readout
 * excludes by joining to that very table.
 */
export function recordFunnelExposure(arm: string): void {
  try {
    if (getAutoProps('BulkDownloadFlags')?.postPackForced) return;
  } catch {
    return;
  }

  recordExtensionBucketing(TEST_KEY, arm);
}
